import { useEffect, useRef, useState, type RefObject } from "react";
import { useMutation, useQuery } from "convex/react";
import { useUser } from "@clerk/react";
import { PageHeader } from "../PageHeader";
import { SectionBoundary } from "../shared/SectionBoundary";
import { usePeople } from "../aiUsage/usePeople";
import { formatWhen, reasonLabel } from "../aiUsage/format";
import { refusalReason } from "../shared/refusalReason";
import { nextLocalMidnight, useNow } from "../aiUsage/useNow";
import { api as convexApi } from "../../../lib/convexApi";

type ToolStatus = "sanctioned" | "review" | "not_allowed";
// The settings' controls, each with its own place for an error.
type Control = "mode" | "retention" | "emergency";

// convex/observability/gateway.ts `policy`, as merged (PR 2).
interface Policy {
  mode: "off" | "observe" | null;
  callRetentionDays: number;
  paused: boolean;
  pausedAt: number | null;
  pausedBy: string | null;
  pauseReason: string | null;
}

// convex/observability/gateway.ts `health`, as merged (PR 2). It reads at most
// CALL_CAP calls and SESSION_CAP sessions, newest first, and says when it hit
// either cap.
interface Health {
  calls: number;
  gatewayErrors: number;
  refused: number;
  sessions: number;
  routed: number;
  // The NOT_ROUTED_SHOWN most recently seen.
  notRouted: Array<{
    sessionId: string;
    userId: string;
    toolName: string;
    reason: string;
    lastSeenAt: number;
  }>;
  // The 10 commonest reasons, then "~other".
  reasons: Array<{ reason: string; sessions: number }>;
  callsTruncated: boolean;
  sessionsTruncated: boolean;
  truncated: boolean;
}

// convex/observability/gateway.ts `tools`, as merged (PR 2): an admin's view.
interface ToolList {
  tools: Array<{
    tool: string;
    toolName: string;
    status: ToolStatus;
    lastSeenAt: number | null;
    statusSetBy: string | null;
    statusSetAt: number | null;
  }>;
  truncated: boolean;
}

// gateway.ts's caps on health's reads.
const CALL_CAP = 1000;
const SESSION_CAP = 600;
// Why the gateway refuses a call (gateway.ts REFUSED_PREFIX: "blocked:paused"
// and "blocked:stopped").
const REFUSED_WHY = "AI traffic was paused or a session was stopped";
const MIN_DAYS = 7;
const MAX_DAYS = 90;
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
// The longest pause reason convex/observability/controls.ts stores, in UTF-8
// bytes (MAX_PAUSE_REASON_BYTES); a longer one is cut there.
const MAX_REASON_BYTES = 200;
const utf8 = new TextEncoder();

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

// health's window: an hour ago, rounded down to the minute. It counts calls
// started in [since, since + 2 hours), so the window has to move while the
// page is open or the call counts would freeze about an hour in.
function hourAgo(now: number = Date.now()): number {
  return Math.floor((now - HOUR_MS) / MINUTE_MS) * MINUTE_MS;
}

function useHourAgo(): number {
  const [since, setSince] = useState(() => hourAgo());
  useEffect(() => {
    // Each minute the same value or the next minute; React skips a render
    // when the value has not changed.
    const id = setInterval(() => setSince(hourAgo()), MINUTE_MS);
    return () => clearInterval(id);
  }, []);
  return since;
}

// When, by whom and why the org was paused, as far as Convex says: "Since
// 10:05, by Bob (drill)." The pauser is decided by id, as the session page
// decides "stopped by you": a member's display name can be any word, "You"
// included.
function describePause(
  policy: Policy,
  me: string | null,
  nameFor: (userId: string) => string,
  now: number
): string {
  let pauser: string | null = null;
  if (policy.pausedBy) pauser = policy.pausedBy === me ? "you" : nameFor(policy.pausedBy);
  const facts = [
    policy.pausedAt ? `Since ${formatWhen(policy.pausedAt, now)}` : null,
    pauser ? `${policy.pausedAt ? "by" : "By"} ${pauser}` : null,
  ]
    .filter(Boolean)
    .join(", ");
  const reason = policy.pauseReason;
  if (reason && facts) return `${facts} (${reason}).`;
  if (reason) return `Reason: ${reason}.`;
  if (facts) return `${facts}.`;
  return "";
}

export function AiGatewayPage() {
  return (
    <>
      <PageHeader title="AI gateway" subtitle="routing, health, tools and data kept" />
      <div className="sr-page-body">
        {/* The role read too: it fails when the org's reads fail (requireOrg),
            and a failure here must keep the header and offer a Retry. */}
        <SectionBoundary label="AI gateway">
          <GatewayBody />
        </SectionBoundary>
      </div>
    </>
  );
}

function GatewayBody() {
  const myRole = useQuery((convexApi as any).billing.queries.myRole, {}) as
    | { isAdmin?: boolean }
    | undefined;
  if (myRole === undefined) return <div className="sr-empty">Loading…</div>;
  if (myRole.isAdmin !== true) {
    return <div className="sr-empty">Only org admins can see and change the AI gateway.</div>;
  }
  return (
    <>
      <SectionBoundary label="Gateway settings">
        <Settings />
      </SectionBoundary>
      <SectionBoundary label="Gateway health">
        <HealthSection />
      </SectionBoundary>
      <SectionBoundary label="AI tools">
        <ToolsSection />
      </SectionBoundary>
    </>
  );
}

function Settings() {
  const policy = useQuery((convexApi as any).observability.gateway.policy, {}) as
    | Policy
    | undefined;
  const setMode = useMutation((convexApi as any).observability.controls.setMode);
  const setRetention = useMutation((convexApi as any).observability.controls.setRetention);
  const pause = useMutation((convexApi as any).observability.controls.pause);
  const resume = useMutation((convexApi as any).observability.controls.resume);
  const { nameFor } = usePeople();
  const { user } = useUser();
  const me = user?.id ?? null;
  const [editingDays, setEditingDays] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"pause" | "resume" | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // A failure is shown under the control that failed.
  const [error, setError] = useState<{ on: Control; text: string } | null>(null);
  // Focus goes back to the button that opened a confirmation once it closes;
  // the button is unmounted while the confirmation is open. A button still
  // disabled (a change on its way) cannot take focus, so focus waits for it,
  // and is not taken back from wherever the admin has moved it meanwhile.
  const emergencyButton = useRef<HTMLButtonElement>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  const focusAfter = useRef<RefObject<HTMLButtonElement | null> | null>(null);
  useEffect(() => {
    const target = focusAfter.current?.current;
    if (!target || target.disabled) return;
    const active = document.activeElement;
    if (!active || active === document.body) target.focus();
    focusAfter.current = null;
  });
  // A confirmation for the state the org is no longer in (another admin
  // paused or resumed meanwhile) is dropped for good, not kept to reopen when
  // the state turns back, and focus goes to the button in its place.
  const stale = policy !== undefined && confirm !== null && (confirm === "pause") === policy.paused;
  useEffect(() => {
    if (stale) {
      focusAfter.current = emergencyButton;
      setConfirm(null);
    }
  }, [stale]);
  // Times shown as today's ("Since 10:05") gain their date at local midnight.
  const now = useNow(nextLocalMidnight);
  if (policy === undefined) return <div className="sr-empty">Loading gateway settings…</div>;

  async function run(
    on: Control,
    action: () => Promise<unknown>,
    fallback: string
  ): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (e) {
      setError({ on, text: refusalReason(e, fallback) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  function closeConfirm() {
    focusAfter.current = emergencyButton;
    setConfirm(null);
  }

  function closeEdit() {
    focusAfter.current = editButton;
    setEditingDays(null);
  }

  const errorFor = (on: Control) =>
    error?.on === on ? (
      <div className="sr-ai-error" role="alert">
        {error.text}
      </div>
    ) : null;

  const days = editingDays === null ? null : Number(editingDays);
  const daysValid =
    days !== null &&
    editingDays!.trim() !== "" &&
    Number.isInteger(days) &&
    days >= MIN_DAYS &&
    days <= MAX_DAYS;
  // A stale confirmation is not offered while the effect above drops it.
  const confirming = stale ? null : confirm;
  const pauseWho = describePause(policy, me, nameFor, now);
  // As the server measures it: trimmed first (convex/observability/controls.ts).
  const reasonBytes = utf8.encode(reason.trim()).length;

  return (
    <>
      {policy.paused && (
        <div className="sr-ai-flag is-deny" role="status">
          <strong>AI traffic is paused.</strong>
          {pauseWho ? ` ${pauseWho}` : ""} Every model call routed through the gateway is refused
          until an admin resumes. Terminals and chats stay open.
        </div>
      )}

      <h2 className="sr-section-title">Mode</h2>
      <div className="sr-ai-setting">
        <div>
          <div>Route and record AI traffic</div>
          <div className="sr-ai-note">
            In Observe, Steerly terminals and chats running Claude Code send their model calls
            through the gateway, which records each call's model, tokens and cost. Prompts and
            replies are not stored. Other AI tools are not routed yet: Steerly lists their sessions
            but does not see their model calls.
          </div>
          {/* A chat spawns its CLI for each turn, routed by the mode at that send
              (src/chatEngine.js); a running turn keeps its route, as a terminal keeps
              the one it started with. */}
          <div className="sr-ai-note">
            A change applies to terminals opened after it and to each chat's next turn. Open
            terminals and running chat turns keep how they started.
          </div>
          {policy.mode === null && (
            <div className="sr-ai-note">
              Not set yet: each Steerly app follows its own AGENTGUARD_GATEWAY_MODE setting until
              you choose.
            </div>
          )}
          {policy.mode === "off" && (
            <div className="sr-ai-note">
              Off stops routing new terminals and new chat turns. Terminals and chat turns already
              routed keep working and are still recorded.
            </div>
          )}
        </div>
        <div className="sr-seg" role="group" aria-label="Gateway mode">
          {(["off", "observe"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`sr-seg__item${policy.mode === m ? " is-active" : ""}`}
              aria-pressed={policy.mode === m}
              disabled={busy}
              onClick={() =>
                void run("mode", () => setMode({ mode: m }), "Could not change the mode.")
              }
            >
              {m === "off" ? "Off" : "Observe"}
            </button>
          ))}
        </div>
      </div>

      {errorFor("mode")}

      <h2 className="sr-section-title">Data kept</h2>
      <div className="sr-ai-setting">
        <div>
          <div>
            Call details: {policy.callRetentionDays} days. Sessions and daily totals: 13 months.
          </div>
          <div className="sr-ai-note">
            Prompts and replies are never stored. A call's details are metadata, such as its model,
            tokens, cost, timing and outcome.
          </div>
        </div>
        {editingDays === null ? (
          <button
            ref={editButton}
            type="button"
            className="sr-btn sr-btn--ghost"
            onClick={() => {
              setError(null);
              setEditingDays(String(policy.callRetentionDays));
            }}
          >
            Edit
          </button>
        ) : (
          <span className="sr-row-confirm">
            <input
              aria-label="Call retention in days"
              autoFocus
              className="sr-select"
              type="number"
              min={MIN_DAYS}
              max={MAX_DAYS}
              step={1}
              value={editingDays}
              onChange={(e) => setEditingDays(e.target.value)}
            />
            <button
              type="button"
              className="sr-btn sr-btn--ghost"
              onClick={closeEdit}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sr-btn"
              disabled={busy || !daysValid}
              onClick={async () => {
                if (
                  days !== null &&
                  (await run(
                    "retention",
                    () => setRetention({ days }),
                    "Could not change the retention."
                  ))
                ) {
                  closeEdit();
                }
              }}
            >
              Save
            </button>
          </span>
        )}
      </div>
      {editingDays !== null && !daysValid && (
        <div className="sr-ai-note">Choose a whole number of days from 7 to 90.</div>
      )}
      {daysValid && days < policy.callRetentionDays && (
        <div className="sr-ai-note">
          Shortening deletes stored call details older than {days} days shortly after you save. A
          call Steerly received in the last day is kept until that day is up.
        </div>
      )}
      {daysValid && days > policy.callRetentionDays && (
        <div className="sr-ai-note">
          Lengthening keeps stored call details longer. Details already deleted do not come back.
        </div>
      )}

      {errorFor("retention")}

      <h2 className="sr-section-title">Emergency</h2>
      <div className="sr-ai-setting">
        <div>
          <div>{policy.paused ? "Resume AI traffic" : "Pause all AI traffic"}</div>
          <div className="sr-ai-note">
            {policy.paused
              ? "Model calls routed through the gateway go through again."
              : "Every model call routed through the gateway in the org is refused, with an error that says AI traffic is paused, until an admin resumes, or the org's Security plan ends. That includes the next message of a chat routed through the gateway. Each member's Steerly app applies the pause once it is online. Terminals and chats stay open. AI tools whose model calls do not go through the gateway keep working."}
          </div>
        </div>
        {confirming === null && (
          // Disabled while a change is on its way, as the mode buttons and
          // the confirmations' own buttons are: a confirmation dropped as
          // stale while a pause or resume is on its way brings this button
          // back, and one opened then would take that change's late answer
          // (its refusal shown under it, or its success closing it
          // unconfirmed).
          <button
            ref={emergencyButton}
            type="button"
            className={policy.paused ? "sr-btn" : "sr-btn sr-btn--danger"}
            disabled={busy}
            onClick={() => {
              setError(null);
              setConfirm(policy.paused ? "resume" : "pause");
            }}
          >
            {policy.paused ? "Resume" : "Pause"}
          </button>
        )}
      </div>
      {confirming === "pause" && (
        <div className="sr-ai-flag is-deny sr-ai-confirm" role="group" aria-label="Confirm pause">
          {/* The label is the field's accessible name: the reason reaches every
              member whose call is refused, in their agent's error (PR 3b). */}
          <label className="sr-ai-note">
            Reason (optional). Shown to the people whose calls are refused, in their agent's error,
            and kept in the audit log.{" "}
            <input
              autoFocus
              className="sr-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {reasonBytes > MAX_REASON_BYTES && (
            <div className="sr-ai-note">
              This reason is {reasonBytes} bytes; Steerly keeps about the first {MAX_REASON_BYTES}.
            </div>
          )}
          <span className="sr-row-confirm">
            <button
              type="button"
              className="sr-btn sr-btn--ghost"
              onClick={closeConfirm}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sr-btn sr-btn--danger"
              disabled={busy}
              onClick={async () => {
                const args = reason.trim() ? { reason: reason.trim() } : {};
                if (await run("emergency", () => pause(args), "Could not pause AI traffic.")) {
                  closeConfirm();
                  setReason("");
                }
              }}
            >
              Confirm pause
            </button>
          </span>
        </div>
      )}
      {confirming === "resume" && (
        <div className="sr-ai-flag is-ask sr-ai-confirm" role="group" aria-label="Confirm resume">
          <span className="sr-row-confirm">
            <button
              type="button"
              className="sr-btn sr-btn--ghost"
              onClick={closeConfirm}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              autoFocus
              type="button"
              className="sr-btn"
              disabled={busy}
              onClick={async () => {
                if (await run("emergency", () => resume({}), "Could not resume AI traffic.")) {
                  closeConfirm();
                }
              }}
            >
              Confirm resume
            </button>
          </span>
        </div>
      )}
      {errorFor("emergency")}
    </>
  );
}

function HealthSection() {
  const since = useHourAgo();
  const fresh = useQuery((convexApi as any).observability.gateway.health, { since }) as
    | Health
    | undefined;
  // A new window is a new subscription, which answers undefined until its
  // first result; keep showing the last minute's figures meanwhile. A ref,
  // not state: storing each result in state would render again for every
  // result object, and keeping the latest one needs no render of its own.
  const kept = useRef<Health | undefined>(undefined);
  if (fresh !== undefined) kept.current = fresh;
  const h = fresh ?? kept.current;
  const { nameFor } = usePeople();
  const [show, setShow] = useState(false);
  if (h === undefined) return <div className="sr-empty">Loading gateway health…</div>;

  // When health hit its cap on calls, its counts cover the newest CALL_CAP.
  const inNewest = h.callsTruncated
    ? ` in the newest ${CALL_CAP.toLocaleString("en-US")} calls`
    : "";
  const notRouted = h.sessions - h.routed;
  const quiet = h.sessions === 0 && h.calls === 0 && h.refused === 0 && !h.callsTruncated;
  return (
    <>
      <h2 className="sr-section-title">Health, last hour</h2>
      <div className="sr-ai-note">
        Counts only the model calls routed through the gateway. AI tools that are not routed do not
        appear in the call counts.
      </div>
      {quiet ? (
        <div className="sr-empty">
          No sessions in the last hour. Sessions appear once someone opens a Claude Code terminal or
          chat with the mode on Observe.
        </div>
      ) : (
        <>
          <div className="sr-ai-setting">
            {/* Capped, the newest CALL_CAP calls may all have been refused, so
                the headline counts every call that reached the gateway. */}
            <div>
              {h.callsTruncated
                ? `${CALL_CAP.toLocaleString("en-US")}+ model calls reached the gateway`
                : `${plural(h.calls, "model call", "model calls")} routed`}
            </div>
            <span className={`sr-pill ${h.gatewayErrors ? "is-crit" : "is-ok"}`}>
              {h.gatewayErrors
                ? `${plural(h.gatewayErrors, "gateway error", "gateway errors")}${inNewest}`
                : `no gateway errors${inNewest}`}
            </span>
          </div>
          {h.callsTruncated ? (
            <div className="sr-ai-setting">
              <div>
                Of the newest {CALL_CAP.toLocaleString("en-US")}: {h.calls.toLocaleString("en-US")}{" "}
                routed
                {h.refused > 0
                  ? `, ${h.refused.toLocaleString("en-US")} refused because ${REFUSED_WHY}`
                  : ""}
              </div>
            </div>
          ) : (
            h.refused > 0 && (
              <div className="sr-ai-setting">
                <div>
                  {plural(h.refused, "call", "calls")} refused because {REFUSED_WHY}
                </div>
              </div>
            )
          )}
          <div className="sr-ai-setting">
            <div>
              <div>
                {plural(h.routed, "session", "sessions")} routed,{" "}
                {notRouted.toLocaleString("en-US")} not
              </div>
              {h.reasons.length > 0 && (
                <div className="sr-ai-note">
                  Not routed:{" "}
                  {h.reasons.map((r) => `${reasonLabel(r.reason)} (${r.sessions})`).join(", ")}
                </div>
              )}
            </div>
            {h.notRouted.length > 0 && (
              <button
                type="button"
                className="sr-btn sr-btn--ghost"
                aria-expanded={show}
                onClick={() => setShow(!show)}
              >
                {show ? "Hide not-routed sessions" : "Show not-routed sessions"}
              </button>
            )}
          </div>
          {show && (
            <>
              <table className="sr-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Tool</th>
                    <th>Why not routed</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {h.notRouted.map((r) => (
                    <tr key={r.sessionId}>
                      <td>{nameFor(r.userId)}</td>
                      <td>{r.toolName}</td>
                      <td>{reasonLabel(r.reason)}</td>
                      <td>{formatWhen(r.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {h.notRouted.length < notRouted && (
                <div className="sr-ai-note">
                  The {h.notRouted.length} most recently seen are listed.
                </div>
              )}
            </>
          )}
        </>
      )}
      {h.sessionsTruncated && (
        <div className="sr-ai-note">
          A busy hour: the session counts cover the {SESSION_CAP} most recently seen sessions.
        </div>
      )}
    </>
  );
}

function ToolsSection() {
  const list = useQuery((convexApi as any).observability.gateway.tools, {}) as ToolList | undefined;
  const setToolStatus = useMutation((convexApi as any).observability.controls.setToolStatus);
  const { nameFor } = usePeople();
  // The tools whose status is saving. Each tool is its own request, so another
  // tool can change meanwhile; a tool's second change waits for its first.
  const [busyTools, setBusyTools] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  // Times shown as today's gain their date at local midnight.
  const now = useNow(nextLocalMidnight);
  if (list === undefined) return <div className="sr-empty">Loading AI tools…</div>;

  async function change(tool: string, status: ToolStatus) {
    if (busyTools.has(tool)) return;
    setBusyTools((prev) => new Set(prev).add(tool));
    setError(null);
    try {
      await setToolStatus({ tool, status });
    } catch (e) {
      setError(refusalReason(e, "Could not change the status."));
    } finally {
      setBusyTools((prev) => {
        const next = new Set(prev);
        next.delete(tool);
        return next;
      });
    }
  }

  return (
    <>
      <h2 className="sr-section-title">AI tools</h2>
      <div className="sr-ai-note">
        A tool marked "not allowed" or "review" is flagged on the AI overview when it is used. The
        status does not block the tool.
      </div>
      {list.tools.length === 0 ? (
        <div className="sr-empty">
          No AI tools seen yet. Steerly lists a tool once someone opens it in a Steerly terminal or
          chat.
        </div>
      ) : (
        <table className="sr-table">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Status</th>
              <th>Last seen</th>
              <th>Status set by</th>
            </tr>
          </thead>
          <tbody>
            {list.tools.map((t) => (
              <tr key={t.tool}>
                <td>{t.toolName}</td>
                <td>
                  <select
                    aria-label={`Status of ${t.toolName}`}
                    className="sr-select"
                    value={t.status}
                    disabled={busyTools.has(t.tool)}
                    onChange={(e) => void change(t.tool, e.target.value as ToolStatus)}
                  >
                    <option value="sanctioned">sanctioned</option>
                    <option value="review">review</option>
                    <option value="not_allowed">not allowed</option>
                  </select>
                </td>
                <td>{t.lastSeenAt ? formatWhen(t.lastSeenAt, now) : ""}</td>
                <td>
                  {t.statusSetBy
                    ? `${nameFor(t.statusSetBy)}${t.statusSetAt ? `, ${formatWhen(t.statusSetAt, now)}` : ""}`
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list.truncated && (
        <div className="sr-ai-note">
          Steerly has seen more tools than this list shows: the known tools, then the most recently
          seen others.
        </div>
      )}
      {error && (
        <div className="sr-ai-error" role="alert">
          {error}
        </div>
      )}
    </>
  );
}
